// WHY a plan has stopped (#5102) — the derivation behind
// `PlanSummarySchema.stopReason`. Pure: reads a plan's status and its groups'
// dispositions, returns the one reason worth showing on the plan-list row.
//
// Prior art for "the worst group decides" is `plan-stuck.ts`
// (`PLAN_GROUP_STUCK_PRECEDENCE`, #4020). That module answers a different
// question — the funnel stage for operator scripts — and is not changed here.

import { TaskHoldReason, type TaskHoldReason as TaskHoldReasonType } from '../entities/task';
import {
  PlanGroupDisposition,
  PlanGroupStallReason,
  type PlanGroupStallReason as PlanGroupStallReasonType,
  PlanStatus,
  type PlanStopReason,
} from './plan-projection';

/**
 * WHICH STOPPED GROUP DECIDES — total over BOTH reason vocabularies (the
 * `Record` discipline of `PLAN_GROUP_STALL_FINALITY`), so a new hold or stall
 * reason fails the build until it is ranked. The lowest rank wins, and every
 * non-null rank is distinct, so two reasons never tie.
 *
 * The order is "a person must act" first, then "dead", then "clears on its
 * own":
 *
 *   0      `plan_paused` — a person paused the epic, so that is why it stopped (#6748)
 *   1-4    a key or model the account must supply — the key-outage epic's subject
 *   5-9    other person-only remedies
 *   10-13  terminal stalls: the slice needs re-authoring or re-dispatch
 *   14-28  self-clearing waits
 *   null   `upstream_group_unmerged` — never a plan-level reason: it is always
 *          a consequence of another group, and that group is either stopped
 *          (its own reason is reported) or still moving (the plan has not
 *          stopped).
 */
export const PLAN_STOP_REASON_RANK: Record<
  TaskHoldReasonType | PlanGroupStallReasonType,
  number | null
> = {
  // A person paused the epic (#6748): the plan stopped because someone asked it to, so that
  // is the answer whatever else its slices also wait on.
  [TaskHoldReason.PlanPaused]: 0,
  // A person must act: a key or model the account must supply.
  [TaskHoldReason.NoUsableKeys]: 1,
  [TaskHoldReason.ProviderKeyRefused]: 2,
  [TaskHoldReason.ReviewAwaitingModel]: 3,
  // The run-start screen needs a non-donated key the account does not hold (09-10).
  [TaskHoldReason.NoScreenKey]: 4,
  // Other person-only remedies.
  [TaskHoldReason.RepoNotOnboarded]: 5,
  [TaskHoldReason.ModeDisabled]: 6,
  // Every online machine has the task's role switched off (#7008): the account's switch to turn back on.
  [TaskHoldReason.NoRoleMachine]: 6.5,
  [TaskHoldReason.SeatCapped]: 7,
  [TaskHoldReason.RepoEnvFault]: 8,
  // Every attempt met an unprovisioned box: fix the box, then `task redispatch`.
  [TaskHoldReason.EnvFaultExhausted]: 9,
  // Terminal stalls: the slice needs re-authoring or re-dispatch.
  [PlanGroupStallReason.TaskFailed]: 10,
  [PlanGroupStallReason.NoPrLink]: 11,
  [PlanGroupStallReason.PrClosedUnmerged]: 12,
  [PlanGroupStallReason.Cancelled]: 13,
  // Self-clearing waits.
  [TaskHoldReason.CredentialCooling]: 14,
  [TaskHoldReason.PoolCooling]: 15,
  [TaskHoldReason.KeySaturated]: 16,
  [TaskHoldReason.ProviderCapacity]: 17,
  [TaskHoldReason.ResumeBackoff]: 18,
  [TaskHoldReason.AreaBusy]: 19,
  [TaskHoldReason.NoCapableBox]: 20,
  [TaskHoldReason.NoConverseCapacity]: 21,
  [TaskHoldReason.NoServiceCapacity]: 22,
  [TaskHoldReason.BoxFaultExcluded]: 23,
  // A resumed slice waiting for the machine that saved its work (#6748): that machine's next
  // pass, or the pin cleared — transient, beside `box_fault_excluded`. Between two ranks, so
  // it displaces no older one.
  [TaskHoldReason.MachinePinned]: 23.5,
  // A person is pushing to the PR; the round starts once the branch is quiet.
  [TaskHoldReason.PrQuietWindow]: 24,
  // The lane's Agents-page selection waits on its own key: clears on a clock, or at once
  // when the selection changes. Last, so it never displaces an older rank.
  [TaskHoldReason.SelectionWaiting]: 25,
  // The error's public issue is being filed; a 10-minute clock at most. Last, same reason.
  [TaskHoldReason.ErrorIssuePending]: 26,
  // A slot held for higher-priority work; self-clearing (#6711). Last, same reason.
  [TaskHoldReason.KeyReserved]: 27,
  // A review waiting for its head's CI to settle; a CI completion or a 5-minute clock
  // (#6718). Appended after `key_reserved`, so it displaces no older rank.
  [TaskHoldReason.CiPending]: 28,
  // Always a consequence of another group — see the docblock.
  [TaskHoldReason.UpstreamGroupUnmerged]: null,
  // The same: a consequence of another group (its deploy), never the plan's own stop.
  [TaskHoldReason.UpstreamLegUndeployed]: null,
};

/** One group's share of the answer, the fields `planStopReason` reads. */
export type PlanStopReasonGroup = {
  readonly disposition: PlanGroupDisposition;
  readonly stallReason: PlanGroupStallReasonType | null;
  readonly heldReason: TaskHoldReasonType | null;
};

/**
 * The plan-level stop reason, PURE and total.
 *
 * `null` unless the plan is `open` — a cancelled or done plan has not stopped,
 * it has ended. `landed` groups are ignored. A `stalled` group contributes its
 * `stallReason`, an `in_flight` group its task's hold; contributions whose
 * rank is `null` are dropped. The winner is the lowest-ranked contribution,
 * and its `groupCount` is how many groups contributed that same reason. When
 * nothing contributes, the answer is `null`.
 */
export function planStopReason(input: {
  planStatus: PlanStatus;
  groups: readonly PlanStopReasonGroup[];
}): PlanStopReason | null {
  if (input.planStatus !== PlanStatus.Open) return null;

  type Contribution = { kind: 'held' | 'stalled'; reason: string; rank: number };
  const contributions: Contribution[] = [];
  for (const g of input.groups) {
    if (g.disposition === PlanGroupDisposition.Stalled && g.stallReason !== null) {
      const rank = PLAN_STOP_REASON_RANK[g.stallReason];
      if (rank !== null) contributions.push({ kind: 'stalled', reason: g.stallReason, rank });
    }
    // `heldReason` arrives already narrowed to pending rows (`holdOf` at the
    // read seam — a non-pending row's column is a fossil); the rank lookup
    // drops `upstream_group_unmerged`, which is never a plan-level reason.
    if (g.disposition === PlanGroupDisposition.InFlight && g.heldReason !== null) {
      const rank = PLAN_STOP_REASON_RANK[g.heldReason];
      if (rank !== null) contributions.push({ kind: 'held', reason: g.heldReason, rank });
    }
  }
  if (contributions.length === 0) return null;

  const winner = contributions.reduce((best, c) => (c.rank < best.rank ? c : best));
  const groupCount = contributions.filter(
    (c) => c.kind === winner.kind && c.reason === winner.reason,
  ).length;
  return winner.kind === 'held'
    ? { kind: 'held', reason: winner.reason as TaskHoldReasonType, groupCount }
    : { kind: 'stalled', reason: winner.reason as PlanGroupStallReasonType, groupCount };
}
